import { useEffect, useRef } from 'react';
import { Bold, Italic, Link as LinkIcon } from 'lucide-react';

/**
 * Mini rich-text editor for short HTML snippets (e.g. an email paragraph).
 *
 * Why a `contentEditable` div instead of TipTap?
 *   The full TipTap-based RichTextEditor is overkill (and heavier) for a
 *   single short paragraph. This component is intentionally tiny and only
 *   exposes Bold / Italic / Link.
 *
 * Props:
 *   value       — current HTML string (controlled-ish)
 *   onChange    — fired on every edit with the latest sanitized HTML
 *   placeholder — empty-state hint
 */
export default function MiniRichTextEditor({
  value,
  onChange,
  placeholder = '',
  'data-testid': dataTestId,
  minHeight = '120px',
}) {
  const editorRef = useRef(null);
  // Skip the next prop->DOM sync once the user starts editing locally,
  // otherwise the caret jumps to the end of the field on every keystroke.
  const skipNextSync = useRef(false);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    if ((value || '') !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => {
    skipNextSync.current = true;
    onChange(editorRef.current?.innerHTML || '');
  };

  const exec = (command, arg) => {
    // Focus first so the command applies to the current selection.
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertLink = () => {
    const sel = window.getSelection();
    const hasSelection = sel && !sel.isCollapsed && editorRef.current?.contains(sel.anchorNode);
    if (!hasSelection) {
      alert('Seleciona primeiro o texto a transformar em link.');
      return;
    }
    const url = window.prompt('URL do link (https://...)', 'https://');
    if (!url || url === 'https://') return;
    exec('createLink', url);
    // Ensure links open in a new tab when sent in an email.
    const links = editorRef.current?.querySelectorAll('a');
    links?.forEach((a) => {
      if (!a.getAttribute('target')) a.setAttribute('target', '_blank');
      if (!a.getAttribute('rel')) a.setAttribute('rel', 'noopener noreferrer');
    });
    emit();
  };

  const onKeyDown = (e) => {
    // Standard shortcuts feel expected on a writing surface
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') { e.preventDefault(); exec('bold'); }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'i') { e.preventDefault(); exec('italic'); }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); insertLink(); }
  };

  return (
    <div className="rounded-md border border-border bg-surface-light overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-surface">
        <button
          type="button"
          onClick={() => exec('bold')}
          data-testid="mini-editor-bold"
          title="Negrito (Ctrl+B)"
          className="p-1.5 rounded hover:bg-ruby/15 text-muted-foreground hover:text-ruby transition-colors"
        >
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => exec('italic')}
          data-testid="mini-editor-italic"
          title="Itálico (Ctrl+I)"
          className="p-1.5 rounded hover:bg-ruby/15 text-muted-foreground hover:text-ruby transition-colors"
        >
          <Italic className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-border mx-1" />
        <button
          type="button"
          onClick={insertLink}
          data-testid="mini-editor-link"
          title="Inserir link (Ctrl+K)"
          className="p-1.5 rounded hover:bg-ruby/15 text-muted-foreground hover:text-ruby transition-colors"
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-testid={dataTestId}
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        onKeyDown={onKeyDown}
        className="mini-richtext-editor px-3 py-2 text-foreground text-sm focus:outline-none"
        style={{ minHeight }}
      />
      <style>{`
        .mini-richtext-editor:empty::before {
          content: attr(data-placeholder);
          color: rgba(148, 148, 148, 0.65);
        }
        .mini-richtext-editor a { color: #DC143C; text-decoration: underline; }
        .mini-richtext-editor strong, .mini-richtext-editor b { color: #fff; }
      `}</style>
    </div>
  );
}
